import React, { useState, useEffect, useRef } from 'react'; // 1. Import necessary hooks

// Helper function to safely get a value from a nested object (no changes here)
const getValueByPath = (obj, path) => {
  if (!path || typeof path !== 'string') return obj;

  try {
    const normalized = path.replace(/\[(\d+)\]/g, '.$1');
    return normalized.split('.').reduce((acc, part) => {
      if (acc == null) return null;
      return acc[part];
    }, obj);
  } catch (err) {
    return null;
  }
};

// Detect any array path c[], c[*], c[0], c[anything]
const isArrayPath = (path) => /\[.*\]/.test(path);

// extract nested array paths (c[].d[].e -> ["c", "c.d"])
const extractNestedArrayPaths = (path) => {
  const parts = path.split('.');
  const paths = [];
  let current = '';

  for (let p of parts) {
    const base = p.includes('[') ? p.split('[')[0] : p;
    current = current ? current + '.' + base : base;

    if (p.includes('[')) {
      paths.push(current);
    }
  }
  return paths;
};

// safely get nested value by path
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, key) => {
    if (acc == null) return null;
    return acc[key];
  }, obj);
};

// cartesian product with merged __indexes
const cartesianProduct = (arrays) => {
  const initial = [{ __indexes: {} }];
  return arrays.reduce((acc, arr) => {
    return acc.flatMap((a) =>
      arr.map((b) => ({
        __indexes: {
          ...(a.__indexes || {}),
          ...(b.__indexes || {}),
        },
      })),
    );
  }, initial);
};

// fully recursive multi-level expansion
const expandItemRecursively = (item, arrayPaths) => {
  if (!arrayPaths || arrayPaths.length === 0) return [item];

  // For each arrayPath, create an array of {__indexes:{path: index}} or skip if not array
  const expansionsPerPath = arrayPaths.map((path) => {
    const arr = getNestedValue(item, path);

    if (!Array.isArray(arr) || arr.length === 0) {
      // If missing or empty, we produce a single "no-op" expansion (no index)
      return [{ __indexes: {} }];
    }

    return arr.map((_, index) => ({
      __indexes: { [path]: index },
    }));
  });

  // cartesian product, merging __indexes correctly
  const combos = cartesianProduct(expansionsPerPath);

  // Map combos into concrete rows where each row gets a combined __indexes map
  return combos.map((c) => ({
    ...item,
    __indexes: c.__indexes,
  }));
};

const DynamicTable = ({ plan, data }) => {
  // --- 2. ADD STATE & REFS FROM JIRA COMPONENT ---
  const popupRef = useRef(null); // Ref to hold the popup window instance
  const intervalRef = useRef(null); // Ref to hold the interval ID for cleanup
  const [isPopupActive, setIsPopupActive] = useState(false); // State to track if popup is open

  // --- 3. ADD LIFECYCLE EFFECT FROM JIRA COMPONENT ---
  // This effect manages the popup's lifecycle. It closes the popup if the main
  // window is refocused or if the component is unmounted.
  useEffect(() => {
    const handleWindowFocus = () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
        setIsPopupActive(false); // Deactivate blur/effects
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    // Cleanup function runs when the component unmounts
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []); // Empty array [] ensures this runs only once.

  // --- 4. ADD POPUP-OPENING FUNCTION FROM JIRA COMPONENT ---
  // This is the core logic for creating a centered popup window.
  const openUrlInPopup = (url) => {
    if (!url) {
      return;
    }

    // Close any previously opened popup
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Define popup size and centered position
    const popupWidth = 1000,
      popupHeight = 600;
    const left = window.screen.width / 2 - popupWidth / 2;
    const top = window.screen.height / 2 - popupHeight / 2;
    const popupFeatures = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},resizable=yes,scrollbars=yes`;

    // Open the new window and store its reference
    popupRef.current = window.open(
      url,
      '_blank',
      popupFeatures.replace(/\s/g, ''),
    );

    if (popupRef.current) {
      setIsPopupActive(true); // Activate effects
    }

    // Periodically check if the user has manually closed the popup
    intervalRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(intervalRef.current);
        popupRef.current = null;
        setIsPopupActive(false); // Deactivate effects
      }
    }, 500);
  };

  // --- Styling Objects (no changes here) ---
  const containerStyle = {
    width: '100%',
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
    marginBottom: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid #3f3f46',
    backgroundColor: '#18181b',
    // Optional: Add a transition for the blur effect
    transition: 'filter 0.3s ease-in-out',
    // Apply blur when popup is active
    filter: isPopupActive ? 'blur(3px)' : 'none',
    pointerEvents: isPopupActive ? 'none' : 'auto',
  };

  const tableStyle = {
    width: '100%',
    minWidth: '600px',
    fontSize: '0.75rem', // text-xs
    textAlign: 'left',
    color: '#d4d4d8', // zinc-300
    borderCollapse: 'collapse',
  };

  const thStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: '#27272a', // zinc-800
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#a1a1aa', // zinc-400
    fontWeight: 500,
    borderBottom: '1px solid #3f3f46', // zinc-700
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const tdStyle = {
    padding: '0.5rem 1rem',
    borderBottom: '1px solid #27272a', // zinc-800
  };

  const cellContentStyle = {
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // --- Component Logic (no major changes) ---
  if (!plan || !data) return null;
  if (!plan.is_table_displayable) return null;
  const items = getValueByPath(data, plan.array_path) || [];
  if (!Array.isArray(items) || items.length === 0) return null;
  const columns = plan.columns;

  // extract ALL nested array paths referenced in columns
  const arrayPathsSet = new Set();

  columns.forEach((col) => {
    if (isArrayPath(col.value_path)) {
      extractNestedArrayPaths(col.value_path).forEach((p) =>
        arrayPathsSet.add(p),
      );
    }
  });

  const arrayPathList = [...arrayPathsSet];
  let expandedItems = [];

  items.forEach((item) => {
    const expanded = expandItemRecursively(item, arrayPathList);
    expandedItems.push(...expanded);
  });

  expandedItems = expandedItems.filter((item) => {
    let hasData = false;

    for (const col of columns) {
      let valPath = col.value_path;

      // inject indexes
      if (isArrayPath(valPath)) {
        extractNestedArrayPaths(valPath).forEach((arrPath) => {
          const idx = item.__indexes?.[arrPath];
          if (idx !== undefined && idx !== null) {
            const safePattern = new RegExp(
              arrPath.replace(/\./g, '\\.') + '\\[[^\\]]*\\]',
            );
            valPath = valPath.replace(safePattern, `${arrPath}[${idx}]`);
          }
        });
      }

      const value = getValueByPath(item, valPath);

      if (value !== null && value !== undefined) {
        hasData = true;
        break;
      }
    }

    return hasData;
  });

  if (expandedItems.length === 0) return null;

  return (
    <div style={containerStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.header} style={thStyle}>
                <div style={cellContentStyle}>{col.header}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {expandedItems.map((item, rowIndex) => {
            const isClickable =
              item && (item.url || item.link || item.issue_url);
            const rowStyle = { cursor: isClickable ? 'pointer' : 'default' };

            return (
              <tr
                key={rowIndex}
                className="hover:bg-zinc-800/50"
                style={rowStyle}
                // --- 5. UPDATE ONCLICK HANDLER ---
                // It now calls the new popup function if a URL exists.
                onClick={() =>
                  isClickable &&
                  openUrlInPopup(item.url || item.link || item.issue_url)
                }
              >
                {columns.map((col) => {
                  let valPath = col.value_path;

                  if (isArrayPath(valPath)) {
                    // For each nested array path referenced in this column, replace its bracketed
                    // part (e.g. c[] or c[0]) with the chosen index from item.__indexes.
                    extractNestedArrayPaths(valPath).forEach((arrPath) => {
                      const idx = item.__indexes?.[arrPath];
                      if (idx !== undefined && idx !== null) {
                        // replace the bracketed segment for this arrPath, e.g. replace "c[]" or "c[0]" etc.
                        const safePattern = new RegExp(
                          arrPath.replace(/\./g, '\\.') + '\\[[^\\]]*\\]',
                        );
                        valPath = valPath.replace(
                          safePattern,
                          `${arrPath}[${idx}]`,
                        );
                      }
                    });
                  }

                  const value = getValueByPath(item, valPath);

                  return (
                    <td key={col.header} style={tdStyle}>
                      <div style={cellContentStyle}>
                        {String(value ?? 'N/A')}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicTable;
